import { useRef } from "react";
import useDimensions from "react-cool-dimensions";
import { useDispatch, useSelector } from "react-redux";
import "../../index.css";
import {
  addHotspotFromUI,
  locationSetIntent,
  setCurrentFrameIndex,
  setCurrentStep,
  setHeight,
  setHotspotIndexOfClickMessageShown,
  setWidth,
} from "../../redux";
import {
  getCurrentFrameIndex,
  getImage,
  getFrameCount,
  getCurrentLayer,
  getCurrentHotspot,
  getHeight,
  getWidth,
  getHotspotNames,
  getCurrentStep,
  getHotspotIndexOfClickMessageShown,
  getHotspotImportInProgress,
  getFitReadyState,
  getDragging,
  getHighlightHotspotNumber,
  getHotspotDisplayState,
} from "../../redux/selectors";
import HotSpotContainer from "../Hotspot";
import {
  clickTravelThreshold,
  angleSensitivityPxPerDegree,
} from "../../config";
import {
  dragClickListener,
  getRelativeCoords,
} from "../../util/browserGeometry";
import { HotspotClickMessageBox } from "../HotspotClickMessageBox";
import { hotspotDisplayStates, steps } from "../../util/constants";
import { isValidPercentage } from "../../util/general";
import { HotspotTooltip } from "../HotspotTooltip/HotspotTooltip";
import { selectHotspot, setDragging } from "../../redux/ui/ui";
import { MarkerContainer } from "../Marker/MarkerContainer";
import { isInteger } from "lodash";
import { getNaturalAspect } from "../../imageStore/imageStore";
import SpinHint from "../SpinHint/SpinHint";
import { HotspotPath } from "../HotspotPath/HotspotPath";

const { abs, ceil, sign } = Math;

function HotspotLocationEditor(props) {
  const { id = "hotspotEditor", style: propStyle, className } = props;
  const canvasRef = useRef();
  const hotspotImportInProgress = useSelector(getHotspotImportInProgress);
  const currentStep = useSelector(getCurrentStep);
  const hotspotNames = useSelector(getHotspotNames);
  const naturalAspect = getNaturalAspect();
  const fitReady = useSelector(getFitReadyState);
  const hotspotIndex = useSelector(getCurrentHotspot);
  const dragging = useSelector(getDragging);
  const highlightHotspotNumber = useSelector(getHighlightHotspotNumber);
  const canAddHotspotWithClick = hotspotNames.length === 0;
  const setProperWidth = ({ width, height }) => {
    const containerAspect = height / width;
    let newWidth = width;
    let newHeight = height;
    if (containerAspect < naturalAspect)
      //container too tall
      newWidth = height / naturalAspect;
    if (containerAspect > naturalAspect)
      // container too wide
      newHeight = width * naturalAspect;
    dispatch(setHeight(newHeight));
    dispatch(setWidth(newWidth));
  };
  const { observe } = useDimensions({
    // from react-cool-dimensions
    onResize: setProperWidth,
  });
  const height = useSelector(getHeight);
  const width = useSelector(getWidth);

  const N = useSelector(getFrameCount);
  const currentFrameIndex = useSelector(getCurrentFrameIndex);
  const image = useSelector(getImage);
  const layer = useSelector(getCurrentLayer);
  const hotspotIndexOfClickMessageShown = useSelector(
    getHotspotIndexOfClickMessageShown
  );
  const dispatch = useDispatch();

  function onDrag(e, { dx, dy }) {
    if (currentStep === "preview" && hotspotIndexOfClickMessageShown !== null) {
      dispatch(setHotspotIndexOfClickMessageShown(null));
      return;
    }
    const frameSensitivityPxPerFrame = Math.max(
      (360 * angleSensitivityPxPerDegree) / N,
      clickTravelThreshold
    ); //to be sure that click is never switching frames
    const dFrameIndex = -Math.floor(dx / frameSensitivityPxPerFrame); // minus sign to conform to SMV behavior
    const frameIndex = dFrameIndex + currentFrameIndex;
    if (dFrameIndex !== window?.SirvHSE?.dFrameIndex) {
      window.SirvHSE.dFrameIndex = dFrameIndex;
      dispatch(setCurrentFrameIndex({ frameIndex }));
    }
    if (!window?.dragging) {
      window.dragging = true;

      dispatch(setDragging(true)); // TODO: consider switching to a global var for this state, since this approach spams many redux actions
    }
  }

  const currentHotspotDisplayState = useSelector((state) =>
    getHotspotDisplayState(state, { hotspotIndex })
  );

  const hotspotPlaceable =
    hotspotIndexOfClickMessageShown == null &&
    currentStep === steps.configureLocations &&
    [
      hotspotDisplayStates.proposed,
      hotspotDisplayStates.none,
      hotspotDisplayStates.hiddenAccepted,
      hotspotDisplayStates.hiddenApproved,
    ].includes(currentHotspotDisplayState);

  function onClick(e) {
    if (
      [
        steps.preview,
        steps.configureHoverAction,
        steps.configureClickAction,
      ].includes(currentStep) &&
      hotspotIndex !== null
    ) {
      dispatch(selectHotspot({ hotspotIndex: null }));
    }

    if (currentStep === steps.pickTheme) {
      dispatch(setCurrentStep(steps.preview));
    }
    if (
      currentStep === steps.preview &&
      hotspotIndexOfClickMessageShown !== null
    ) {
      dispatch(setHotspotIndexOfClickMessageShown(null));
      return;
    }
    const [pointerRelativeToDivX, pointerRelativeToDivY] = getRelativeCoords(
      e,
      e.target
    );

    const x = (100.0 * pointerRelativeToDivX) / width;
    const y = (100.0 * pointerRelativeToDivY) / height;
    const mouseCoordinatesInPercent = { x, y };
    if (!(isValidPercentage(x) && isValidPercentage(y))) {
      return;
    }
    let payload = {
      layer,
      frame: currentFrameIndex,
      mouseCoordinatesInPercent,
    };
    if (canAddHotspotWithClick) {
      dispatch(addHotspotFromUI(payload));
    }
    if (![steps.configureLocations].includes(currentStep)) {
      return;
    }
    if (hotspotIndex === null) {
      return;
    }
    if (!hotspotPlaceable) {
      return;
    }
    payload.hotspotIndex = hotspotIndex;
    dispatch(locationSetIntent(payload));
  }

  function onDragRelease() {
    window.dragging = false;
    dispatch(setDragging(false));
  }

  function pointerDownHandler(e) {
    dragClickListener(e, { onDrag, onClick, onDragRelease });
  }

  const outerStyle = {
    touchAction: "none",
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "relative", // for contained hotspots to be correctly positioned
    margin: "0",
    padding: "24px",
    boxSizing: "border-box",
    // overflow: "hidden",
    ...propStyle,
  };
  const innerStyle = {
    position: "absolute", // for contained hotspots to be correctly positioned
    margin: "0",
    width: `${width}px`,
    height: `${height}px`,
    cursor: dragging
      ? "grabbing"
      : hotspotPlaceable
      ? "crosshair" //"copy"
      : canAddHotspotWithClick
      ? "crosshair"
      : "grab",
  };
  function updateCanvas() {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const ctx = canvas.getContext("2d");
    ctx.globalCompositeOperation = "copy";
    if (image?.complete) {
      ctx.drawImage(image, 0, 0, width, height);
    }
  }

  updateCanvas();

  const handleWheel = (e) => {
    const deltaFrame = sign(e.deltaY) * ceil(abs(e.deltaY * N * 0.0001));
    const frameIndex = currentFrameIndex + deltaFrame;
    if (deltaFrame) {
      dispatch(setCurrentFrameIndex({ frameIndex }));
    }
  };

  return (
    <div
      ref={observe}
      id={id}
      style={outerStyle}
      draggable={false}
      className={className}
    >
      <div
        style={innerStyle}
        onPointerDownCapture={(e) => {
          document.activeElement.blur();
        }}
        onPointerDown={pointerDownHandler}
        onWheelCapture={handleWheel}
      >
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          style={{ position: "absolute" }}
        />
        {[steps.configureLocations].includes(currentStep) && fitReady && (
          <HotspotPath />
        )}
        {!hotspotImportInProgress && isInteger(highlightHotspotNumber) ? (
          <HotSpotContainer hotspotIndex={highlightHotspotNumber} />
        ) : (
          hotspotNames.map((name, i) => (
            <HotSpotContainer key={i} hotspotIndex={i} />
          ))
        )}
        {[steps.configureLocations].includes(currentStep) && (
          <MarkerContainer />
        )}

        <HotspotTooltip hotspotIndex={hotspotIndex} />
        <HotspotClickMessageBox />
        <SpinHint />
      </div>
    </div>
  );
}

export default HotspotLocationEditor;
